import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { TrendingUp, Users, CreditCard, PieChart as PieIcon } from "lucide-react";

/**
 * SaaS lifecycle analytics for the super-admin dashboard.
 * Five charts + snapshot cards sharing a single 30d/90d/12mo range toggle:
 *   1. Signups over time (bar)
 *   2. MRR trend (area/line, $)
 *   3. Active monthly subscribers (line)
 *   4. Signups vs Cancellations per week (grouped bars)
 *   5. Annual vs Monthly split (donut)
 */
export default function SuperAdminAnalytics() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/super/analytics", { params: { range } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range]);

  // Reshape parallel arrays into recharts-friendly [{label, ...}] rows.
  const series = useMemo(() => {
    if (!data) return [];
    return data.labels.map((label, i) => ({
      label,
      signups: data.signups[i] || 0,
      cancellations: data.cancellations[i] || 0,
      net: data.net_growth[i] || 0,
      active_monthly: data.active_monthly_subs[i] || 0,
      mrr: data.mrr[i] || 0,
    }));
  }, [data]);

  const planSplit = useMemo(() => {
    if (!data) return [];
    return [
      { name: "Monthly", value: data.plan_split?.monthly || 0 },
      { name: "Annual",  value: data.plan_split?.annual  || 0 },
    ];
  }, [data]);
  const PLAN_COLORS = ["#0ea5e9", "#8b5cf6"]; // sky-500, violet-500

  const snapshot = data?.snapshot || {};

  return (
    <section className="space-y-4" data-testid="super-analytics-section">
      {/* Range toggle + snapshot pills */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">SaaS Analytics</p>
          <h2 className="text-2xl font-extrabold font-display tracking-tighter text-slate-900 mt-0.5">Growth &amp; Revenue</h2>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="group" data-testid="analytics-range-toggle">
          {[
            { k: "30d",  label: "30d" },
            { k: "90d",  label: "90d" },
            { k: "12mo", label: "12mo" },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setRange(opt.k)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                range === opt.k
                  ? "bg-slate-900 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`range-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Snapshot stat pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-snapshot">
        <StatPill icon={<TrendingUp className="h-4 w-4" />} label="MRR" value={fmtMoney(snapshot.mrr_usd || 0)} tint="emerald" testid="stat-mrr" />
        <StatPill icon={<CreditCard className="h-4 w-4" />} label="Paying" value={snapshot.paying ?? 0} tint="sky" testid="stat-paying" />
        <StatPill icon={<Users className="h-4 w-4" />} label="Trialing" value={snapshot.trialing ?? 0} tint="amber" testid="stat-trialing" />
        <StatPill icon={<PieIcon className="h-4 w-4" />} label="Total tenants" value={snapshot.total_tenants ?? 0} tint="slate" testid="stat-total" />
      </div>

      {loading && <p className="text-xs text-slate-500">Loading…</p>}

      {/* Row 1: Signups + MRR */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Signups" subtitle="New tenants per bucket" testid="chart-signups">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={series} margin={{ top: 10, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="signups" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Monthly Recurring Revenue" subtitle="MRR trend, USD" testid="chart-mrr">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={series} margin={{ top: 10, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `$${v}`} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => [`$${Number(v).toFixed(2)}`, "MRR"]} />
              <Line type="monotone" dataKey="mrr" stroke="#059669" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2: Active monthly subs + Signups vs Cancellations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Active Monthly Subscribers" subtitle="Paying tenants on monthly plans" testid="chart-active-monthly">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={series} margin={{ top: 10, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="active_monthly" stroke="#0ea5e9" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Signups vs Cancellations" subtitle="Net growth per bucket" testid="chart-net-growth">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={series} margin={{ top: 10, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="signups" fill="#0ea5e9" name="Signups" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cancellations" fill="#f43f5e" name="Cancellations" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 3: Annual vs Monthly split (donut) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Annual vs Monthly" subtitle="Interval split of paying tenants" testid="chart-plan-split" className="lg:col-span-1">
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="60%" height={180}>
              <PieChart>
                <Pie
                  data={planSplit}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {planSplit.map((entry, i) => (
                    <Cell key={entry.name} fill={PLAN_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col gap-2 text-xs">
              {planSplit.map((entry, i) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <span className="inline-block h-3 w-3 rounded-sm" style={{ background: PLAN_COLORS[i] }} />
                  <span className="font-semibold text-slate-700">{entry.name}</span>
                  <span className="text-slate-500 tabular-nums">{entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>
        <div className="lg:col-span-2 hidden lg:block" />
      </div>
    </section>
  );
}

function StatPill({ icon, label, value, tint, testid }) {
  const tints = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sky:     "bg-sky-50 text-sky-700 border-sky-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-xl border ${tints[tint] || tints.slate} p-3`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        {icon}
        <p className="text-[10px] font-bold uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-xl font-extrabold tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid, className = "" }) {
  return (
    <Card className={`rounded-2xl border-slate-200 ${className}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="mb-2">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{subtitle}</p>
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}
